import Swal from "sweetalert2";

/**
 * Um aviso com o texto **como texto**.
 *
 * Tinha `html: texto` ao lado do `text`, e o `html` do SweetAlert2 ganha sempre: cada mensagem
 * era desenhada como HTML. As mensagens vêm quase todas do servidor e levam lá dentro coisas
 * que um utilizador escreveu — o nome de um caixa, de uma mesa, de um cliente —, e isso é HTML
 * injectado no browser de quem gere a empresa. Das ~2800 chamadas do painel, **uma** passava
 * HTML de propósito; é para essa que existe o [showPopHtml].
 */
function showPop(titulo: string, texto: string, tipo: "success" | "question" | "error" | "warning" | "info", callback?: (()=> void) | null){
    Swal.fire({
        title: titulo,
        confirmButtonText: `Ok`,
        text: texto,
        //confirmButtonColor: window.cor_primaria,
        //iconColor: tipo === "success" && window.cor_primaria,
        icon: tipo,
    }).then((result) => {
        if(typeof callback === "function"){
            callback();
        }
    });
}

function showPopConfirm(titulo: string, texto: string, tipo: "success" | "question" | "error" | "warning" | "info", callback?: (()=> void) | null, btOk: string = "Sim" , btCancel: string = "Não", cancelcallback: (()=> void) | null = null): Promise<boolean>{
    return Swal.fire({
        title: titulo,
        confirmButtonText: btOk,
        cancelButtonText: btCancel,
        //confirmButtonColor: window.cor_primaria,
        showCancelButton: true,
        text: texto,
        icon: tipo,
    }).then((result) => {
        if(result.value) {
            if(typeof callback === "function"){
                callback();
            }
            return true;
        } else {
            if(typeof cancelcallback === "function"){
                cancelcallback();
            }
            return false;
        }
    });
}

/**
 * O mesmo aviso, com HTML — **só para marcação escrita no código**.
 *
 * Tudo o que venha do servidor ou de um formulário passa primeiro pelo [escaparHtml]; caso
 * contrário use-se o [showPop], que trata o texto como texto.
 */
function showPopHtml(titulo: string, html: string, tipo: "success" | "question" | "error" | "warning" | "info", callback?: (()=> void) | null){
    Swal.fire({
        title: titulo,
        confirmButtonText: `Ok`,
        html: html,
        icon: tipo,
    }).then(() => {
        if(typeof callback === "function"){
            callback();
        }
    });
}

/** Para o que vai dentro de um [showPopHtml] e não foi escrito aqui. */
function escaparHtml(texto: string): string {
    return String(texto ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// API wrapper para manter compatibilidade
export const Pops = {
    // Sem os títulos «Sucesso» e «Erro» (a regra dos textos de ecrã): o ícone já diz o que é e a
    // mensagem diz o que aconteceu.
    success: (message: string, callback?: (() => void) | null) => {
        showPop('', message, 'success', callback);
    },

    error: (message: string, callback?: (() => void) | null) => {
        showPop('', message, 'error', callback);
    },

    warning: (message: string, callback?: (() => void) | null) => {
        showPop('Aviso', message, 'warning', callback);
    },

    info: (message: string, callback?: (() => void) | null) => {
        showPop('Informação', message, 'question', callback);
    },

    confirm: (message: string, onConfirm?: (() => void) | null, onCancel?: (() => void) | null) => {
        return showPopConfirm('Confirmação', message, 'question', onConfirm, 'Sim', 'Não', onCancel);
    },

    /**
     * Alias para confirm (versão em português)
     */
    confirmar: (message: string, onConfirm?: (() => void) | null, onCancel?: (() => void) | null) => {
        return showPopConfirm('Confirmação', message, 'question', onConfirm, 'Sim', 'Não', onCancel);
    },

    /**
     * Mostra um diálogo de input para o utilizador inserir um valor
     * @param title Título do diálogo
     * @param defaultValue Valor padrão do input
     * @param placeholder Placeholder do input
     * @returns O valor inserido ou null se cancelado
     */
    input: async (title: string, defaultValue: string = '', placeholder: string = ''): Promise<string | null> => {
        const result = await Swal.fire({
            title: title,
            input: 'text',
            inputValue: defaultValue,
            inputPlaceholder: placeholder,
            showCancelButton: true,
            confirmButtonText: 'Confirmar',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value || value.trim() === '') {
                    return 'Insere um valor';
                }
                return null;
            }
        });

        if (result.isConfirmed && result.value) {
            return result.value.trim();
        }
        return null;
    },

    /**
     * Mostra um loading overlay
     * @param title Título do loading (opcional)
     */
    showLoading: (title: string = 'A processar...') => {
        Swal.fire({
            title: title,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
    },

    /**
     * Esconde o loading overlay
     */
    hideLoading: () => {
        Swal.close();
    }
};

export {showPop, showPopConfirm, showPopHtml, escaparHtml}
